/**
 * Getypeerde toegang tot `/payment-accounts`.
 *
 * Het geheim gaat er één kant op. Er is geen functie die het ophaalt, want er is
 * geen route die het teruggeeft — ook niet aan degene die het net heeft ingevoerd.
 * Wat er terugkomt is de status, de naam van de rekening en een herkenningshint.
 */
import { call, isRecord, readOptionalText, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type PaymentFailure = Failure
export type PaymentResult<T> = Result<T>

export const PAYMENT_PROVIDERS = ['mollie', 'stripe'] as const
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number]

export interface PaymentAccount {
  readonly id: string
  readonly provider: PaymentProvider
  readonly label: string
  /** `test` of `live`; het verschil is te belangrijk om te verbergen. */
  readonly mode: string
  readonly connectedVia: string
  readonly accountName: string
  readonly status: string
  readonly lastVerifiedAt: string | null
  readonly lastError: string
  readonly residenceIds: readonly string[]
  readonly accountHint: string
}

export interface PaymentAccounts {
  readonly accounts: readonly PaymentAccount[]
  /** False wanneer het platform geen geheimen kan opslaan; dan heeft koppelen geen zin. */
  readonly canConnect: boolean
}

function isProvider(value: unknown): value is PaymentProvider {
  const known: readonly string[] = PAYMENT_PROVIDERS
  return typeof value === 'string' && known.includes(value)
}

function readAccount(value: unknown): PaymentAccount | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const provider = value['provider']
  if (id === '' || !isProvider(provider)) {
    return null
  }

  const residenceIds: string[] = []
  if (Array.isArray(value['residenceIds'])) {
    for (const entry of value['residenceIds']) {
      if (typeof entry === 'string' && entry !== '') {
        residenceIds.push(entry)
      }
    }
  }

  return {
    id,
    provider,
    residenceIds,
    label: readText(value, 'label'),
    mode: readText(value, 'mode'),
    connectedVia: readText(value, 'connectedVia'),
    accountName: readText(value, 'accountName'),
    status: readText(value, 'status'),
    lastVerifiedAt: readOptionalText(value, 'lastVerifiedAt'),
    lastError: readText(value, 'lastError'),
    accountHint: readText(value, 'accountHint'),
  }
}

export async function listPaymentAccounts(
  options: RequestOptions = {},
): Promise<PaymentResult<PaymentAccounts>> {
  return call(
    'GET',
    '/payment-accounts',
    undefined,
    (payload) => {
      if (!isRecord(payload) || !Array.isArray(payload['accounts'])) {
        return null
      }
      const accounts: PaymentAccount[] = []
      for (const entry of payload['accounts']) {
        const account = readAccount(entry)
        if (account !== null) {
          accounts.push(account)
        }
      }
      return { accounts, canConnect: payload['canConnect'] === true }
    },
    options,
  )
}

export async function connectPaymentAccount(body: {
  readonly provider: PaymentProvider
  readonly label: string
  readonly secret: string
}): Promise<PaymentResult<PaymentAccount>> {
  return call('POST', '/payment-accounts', body, (payload) =>
    isRecord(payload) ? readAccount(payload['account']) : null,
  )
}

export async function verifyPaymentAccount(id: string): Promise<PaymentResult<PaymentAccount>> {
  return call('POST', `/payment-accounts/${encodeURIComponent(id)}/verify`, undefined, (payload) =>
    isRecord(payload) ? readAccount(payload['account']) : null,
  )
}

export async function disconnectPaymentAccount(id: string): Promise<PaymentResult<true>> {
  return call('DELETE', `/payment-accounts/${encodeURIComponent(id)}`, undefined, () => true)
}

/** Vertaalsleutel bij een mislukte aanroep. */
export function paymentErrorMessageKey(failure: PaymentFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.payments.error.rejected'
    case 'not_found':
      return 'dashboard.payments.error.notFound'
    case 'forbidden':
      return 'dashboard.payments.error.forbidden'
    case 'conflict':
      return 'dashboard.payments.error.conflict'
    case 'rate_limited':
      return 'dashboard.payments.error.rateLimited'
    default:
      return 'dashboard.payments.error.unavailable'
  }
}
